/**
 * weatherMotionEstimator.ts — Block-matching motion estimation for radar frames.
 *
 * Given two consecutive MRMS frames (encoded as Uint8Array of category bytes),
 * compute a grid of 2D displacement vectors indicating how precipitation has
 * moved from frame A to frame B. The result is used by the raster layer
 * shader to warp pixels along their motion trajectories during time
 * interpolation — producing real motion rather than cross-fade.
 *
 * ── Algorithm ──────────────────────────────────────────────────────────────
 *
 * 1. Downsample both frames into small "signal" images by computing a
 *    precip-presence mask: 1 where data exists (byte > 0 and < 255), 0
 *    otherwise. The mask smooths away category detail so block matching
 *    tracks WHERE precip is, not what type.
 *
 * 2. For each coarse grid cell (e.g., 64×42 grid over the frame), extract
 *    a small patch from frame A and search a bounded window in frame B
 *    for the patch that best matches (minimum sum-of-absolute-differences).
 *
 * 3. Post-process the vector field: median filter to reject outliers,
 *    zero out vectors where source block has no data to match against.
 *
 * The output grid is packed into RGBA bytes (with displacement components
 * encoded as signed 8-bit values) so it can be uploaded as a small texture
 * to the GPU and sampled with bilinear filtering for per-pixel warping.
 *
 * ── Performance budget ─────────────────────────────────────────────────────
 *
 * MRMS source: 3276×2139 (after downsample). Search grid: 64×42 = 2688 blocks.
 * Search window: ±16 pixels = 33×33 candidate positions per block.
 * Patch size: 32×32. Cost per block ≈ 33×33×32×32 = 1.1M ops. Total ≈ 3 Gops.
 *
 * We accelerate by:
 *   - Working on a downsampled signal image (8× smaller per axis = 64× fewer ops)
 *   - Using a single-channel mask (1 byte per pixel instead of 4)
 *   - Early termination in the SAD search when a good match is found
 *
 * Expected runtime: ~50-150ms per frame-pair on modern hardware.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MotionField {
  /** Width of the flow vector grid (columns) */
  width: number;
  /** Height of the flow vector grid (rows) */
  height: number;
  /**
   * RGBA-packed flow vectors, ready for GPU upload.
   * R = (dx + 127) clamped to [0, 254], G = (dy + 127) clamped to [0, 254].
   * B = confidence (0 = no match, 255 = perfect match).
   * A = 255 (unused, keeps byte alignment).
   *
   * Displacement is expressed in SOURCE-TEXTURE PIXELS, scaled to the full
   * resolution (not the downsampled mask). Shader must scale by 1/texSize
   * to get UV-space offset.
   */
  rgba: Uint8Array;
  /**
   * Maximum displacement magnitude in the field, in source-texture pixels.
   * Useful for debug logging.
   */
  maxMagnitude: number;
}

// ─── Configuration ──────────────────────────────────────────────────────────

/**
 * Tuning parameters. Exposed as a const block so they can be tweaked after
 * initial testing without editing algorithm code.
 */
const MOTION_CONFIG = {
  /** Downsample factor for the signal image (higher = faster, less precise). */
  signalDownsample: 8,

  /** Size of the output flow vector grid. Smaller = faster but coarser. */
  flowGridW: 64,
  flowGridH: 42,

  /** Block size for SAD matching, in signal-image pixels. */
  blockSize: 16,

  /**
   * Search radius in signal-image pixels. A radius of 12 at signalDownsample=8
   * corresponds to ±96 source pixels — enough for ~1-2 MRMS cells' worth of
   * motion over 15 minutes.
   */
  searchRadius: 12,

  /**
   * Minimum non-zero fraction in a source block for its motion vector to be
   * trusted. With a binary signal, 3% = "at least ~5 cells out of 256 in the
   * block have precip" — enough to match against and track motion.
   */
  minBlockOccupancy: 0.03,

  /** Cap on reported displacement magnitude, in source pixels. */
  maxDisplacement: 128,
};

// ─── Signal extraction ─────────────────────────────────────────────────────

/**
 * Extract a downsampled "precip mask" from a raw MRMS frame.
 *
 * The source is a Uint8Array of RGBA bytes (width*height*4) where the R
 * channel contains the category byte. We downsample by averaging N×N patches
 * into a single byte indicating "fraction of this patch that has any precip."
 *
 * The mask suppresses category detail — a rain/sleet/snow boundary becomes
 * a single continuous "precip region" — which is exactly what we want for
 * motion tracking. The cells move as a whole, not their internal category
 * boundaries.
 */
function extractSignal(
  rawRgba: Uint8Array,
  width: number,
  height: number,
  downsample: number,
): { signal: Uint8Array; sigW: number; sigH: number } {
  const sigW = Math.max(1, Math.floor(width / downsample));
  const sigH = Math.max(1, Math.floor(height / downsample));
  const signal = new Uint8Array(sigW * sigH);

  // BINARY MASK: 255 if any precip in this downsampled patch, 0 otherwise.
  // We previously used fractional coverage, but that produced unstable
  // values along cell edges (boiling convection, small-scale variability)
  // that made SAD-based block matching think storms weren't moving even
  // when they clearly were. A binary mask answers the simpler question —
  // "where is there any precip?" — which is stable across 15-min frames.
  for (let sy = 0; sy < sigH; sy++) {
    for (let sx = 0; sx < sigW; sx++) {
      let hits = 0;
      const y0 = sy * downsample;
      const x0 = sx * downsample;
      for (let dy = 0; dy < downsample; dy++) {
        const py = y0 + dy;
        if (py >= height) continue;
        const rowStart = py * width * 4;
        for (let dx = 0; dx < downsample; dx++) {
          const px = x0 + dx;
          if (px >= width) continue;
          const b = rawRgba[rowStart + px * 4]; // R channel
          if (b > 0 && b < 255) { hits++; break; }
        }
        if (hits > 0) break;
      }
      signal[sy * sigW + sx] = hits > 0 ? 255 : 0;
    }
  }

  return { signal, sigW, sigH };
}

// ─── Block matching ────────────────────────────────────────────────────────

/**
 * Sum of Absolute Differences between a block in frame A and a candidate
 * block in frame B. Lower = better match.
 *
 * Returns a large sentinel value when the candidate position would read
 * outside the signal image (caller handles the "no match" case).
 */
function computeSAD(
  sigA: Uint8Array,
  sigB: Uint8Array,
  sigW: number,
  sigH: number,
  ax: number,  // frame-A block top-left x
  ay: number,  // frame-A block top-left y
  bx: number,  // frame-B block top-left x
  by: number,  // frame-B block top-left y
  blockSize: number,
): number {
  // Bail out-of-bounds
  if (ax < 0 || ay < 0 || bx < 0 || by < 0) return Number.POSITIVE_INFINITY;
  if (ax + blockSize > sigW || ay + blockSize > sigH) return Number.POSITIVE_INFINITY;
  if (bx + blockSize > sigW || by + blockSize > sigH) return Number.POSITIVE_INFINITY;

  let sum = 0;
  for (let dy = 0; dy < blockSize; dy++) {
    const aRow = (ay + dy) * sigW + ax;
    const bRow = (by + dy) * sigW + bx;
    for (let dx = 0; dx < blockSize; dx++) {
      sum += Math.abs(sigA[aRow + dx] - sigB[bRow + dx]);
    }
  }
  return sum;
}

/**
 * Find the best-matching block position in frame B for a block centered at
 * (acx, acy) in frame A. Returns displacement in SIGNAL-IMAGE pixels.
 */
function findBestMatch(
  sigA: Uint8Array,
  sigB: Uint8Array,
  sigW: number,
  sigH: number,
  acx: number,
  acy: number,
  blockSize: number,
  searchRadius: number,
): { dx: number; dy: number; confidence: number } {
  // Block top-left in A
  const halfBlock = blockSize >> 1;
  const ax = acx - halfBlock;
  const ay = acy - halfBlock;

  // First, compute occupancy of the source block — how many cells have precip.
  // If mostly empty, there's nothing to track.
  let occupancy = 0;
  for (let dy = 0; dy < blockSize; dy++) {
    const py = ay + dy;
    if (py < 0 || py >= sigH) continue;
    for (let dx = 0; dx < blockSize; dx++) {
      const px = ax + dx;
      if (px < 0 || px >= sigW) continue;
      if (sigA[py * sigW + px] > 0) occupancy++;
    }
  }
  const total = blockSize * blockSize;
  const occupancyFrac = occupancy / total;
  if (occupancyFrac < MOTION_CONFIG.minBlockOccupancy) {
    return { dx: 0, dy: 0, confidence: 0 };
  }

  // Search window: ±searchRadius around the original block position in B.
  // (i.e., assume motion is bounded and search around the same coordinates)
  let bestSAD = Number.POSITIVE_INFINITY;
  let bestDx = 0;
  let bestDy = 0;

  for (let sy = -searchRadius; sy <= searchRadius; sy++) {
    for (let sx = -searchRadius; sx <= searchRadius; sx++) {
      const sad = computeSAD(
        sigA, sigB, sigW, sigH,
        ax, ay,
        ax + sx, ay + sy,
        blockSize,
      );
      if (sad < bestSAD) {
        bestSAD = sad;
        bestDx = sx;
        bestDy = sy;
      }
    }
  }

  if (!isFinite(bestSAD)) {
    return { dx: 0, dy: 0, confidence: 0 };
  }

  // Confidence: with a BINARY signal (0 or 255), SAD per-mismatch is always 255.
  // So SAD = 255 × numMismatches. Total possible mismatches = block area.
  // Confidence = 1 - (numMismatches / blockArea) = how well the blocks agree.
  // We scale by a tolerance factor so 20% mismatches → confidence 0 (genuinely
  // different blocks) but 5% mismatches → confidence 0.75 (good match despite
  // minor storm evolution).
  const numMismatches = bestSAD / 255;
  const mismatchFrac = numMismatches / total;
  const confidence = Math.max(0, Math.min(1, 1 - mismatchFrac / 0.25));

  return { dx: bestDx, dy: bestDy, confidence };
}

// ─── Vector field post-processing ──────────────────────────────────────────

/**
 * Apply a 3×3 median filter to the flow field to reject outlier vectors.
 * Operates separately on dx and dy components.
 */
function medianFilterField(
  vectors: { dx: number; dy: number; confidence: number }[],
  gridW: number,
  gridH: number,
): { dx: number; dy: number; confidence: number }[] {
  const result = new Array(vectors.length);

  const neighbors: number[] = new Array(9);

  for (let y = 0; y < gridH; y++) {
    for (let x = 0; x < gridW; x++) {
      const idx = y * gridW + x;

      // Collect 3x3 neighborhood, clamped at edges
      const dxs: number[] = [];
      const dys: number[] = [];
      for (let ny = -1; ny <= 1; ny++) {
        for (let nx = -1; nx <= 1; nx++) {
          const cx = Math.max(0, Math.min(gridW - 1, x + nx));
          const cy = Math.max(0, Math.min(gridH - 1, y + ny));
          const n = vectors[cy * gridW + cx];
          // Only include confident-enough neighbors in the median
          if (n.confidence > 0.1) {
            dxs.push(n.dx);
            dys.push(n.dy);
          }
        }
      }

      if (dxs.length === 0) {
        result[idx] = { dx: 0, dy: 0, confidence: vectors[idx].confidence };
        continue;
      }

      dxs.sort((a, b) => a - b);
      dys.sort((a, b) => a - b);
      const midIdx = dxs.length >> 1;

      result[idx] = {
        dx: dxs[midIdx],
        dy: dys[midIdx],
        confidence: vectors[idx].confidence,
      };
    }
  }

  return result;
}

// ─── Packing to RGBA ───────────────────────────────────────────────────────

/**
 * Pack vector field into RGBA bytes for GPU upload.
 *
 * Displacement in SOURCE-TEXTURE pixels (= signal displacement × signal
 * downsample factor). We cap at ±maxDisplacement and encode each component
 * in one byte as (clamped + 127).
 *
 * The GPU shader will decode: displacement = (byte / 255 * 254 - 127) * 2.
 * Actually simpler: (byte / 255) * 2 - 1, then scale by maxDisplacement.
 */
function packField(
  vectors: { dx: number; dy: number; confidence: number }[],
  gridW: number,
  gridH: number,
  signalDownsample: number,
): { rgba: Uint8Array; maxMagnitude: number } {
  const rgba = new Uint8Array(gridW * gridH * 4);
  let maxMag = 0;

  for (let i = 0; i < vectors.length; i++) {
    const v = vectors[i];

    // Convert from signal-image pixels to source-texture pixels
    let sx = v.dx * signalDownsample;
    let sy = v.dy * signalDownsample;

    // Clamp to configured max
    const maxDisp = MOTION_CONFIG.maxDisplacement;
    if (Math.abs(sx) > maxDisp) sx = Math.sign(sx) * maxDisp;
    if (Math.abs(sy) > maxDisp) sy = Math.sign(sy) * maxDisp;

    const mag = Math.sqrt(sx * sx + sy * sy);
    if (mag > maxMag) maxMag = mag;

    // Encode as 0..255 with 127 = zero displacement.
    // Range [-maxDisp..+maxDisp] maps to [-1..+1] at the shader via:
    //   ((byte - 127) / maxDisp) * maxDisp = byte - 127  (roughly)
    // We store in a way that 127 = no motion, 255 = +maxDisp, 0 = -maxDisp.
    const r = Math.round((sx / maxDisp) * 127 + 127);
    const g = Math.round((sy / maxDisp) * 127 + 127);
    const b = Math.round(Math.max(0, Math.min(1, v.confidence)) * 255);

    const p = i * 4;
    rgba[p]     = Math.max(0, Math.min(255, r));
    rgba[p + 1] = Math.max(0, Math.min(255, g));
    rgba[p + 2] = b;
    rgba[p + 3] = 255;
  }

  return { rgba, maxMagnitude: maxMag };
}

// ─── Main entry point ──────────────────────────────────────────────────────

/**
 * Estimate motion between two consecutive radar frames.
 *
 * Both frames must be decoded as RGBA Uint8Arrays from the same prefix (i.e.,
 * same spatial extent & resolution). The R channel is the category byte.
 */
export function estimateMotion(
  frameA: Uint8Array,
  frameB: Uint8Array,
  width: number,
  height: number,
): MotionField {
  const t0 = performance.now();

  // 1. Extract signals
  const { signal: sigA, sigW, sigH } = extractSignal(
    frameA, width, height, MOTION_CONFIG.signalDownsample,
  );
  const { signal: sigB } = extractSignal(
    frameB, width, height, MOTION_CONFIG.signalDownsample,
  );

  // 2. Compute vector field on a coarse grid over the signal image
  const gridW = MOTION_CONFIG.flowGridW;
  const gridH = MOTION_CONFIG.flowGridH;
  const vectors: { dx: number; dy: number; confidence: number }[] = new Array(gridW * gridH);

  for (let gy = 0; gy < gridH; gy++) {
    for (let gx = 0; gx < gridW; gx++) {
      // Sample signal-image coordinates at grid cell center
      const acx = Math.round(((gx + 0.5) / gridW) * sigW);
      const acy = Math.round(((gy + 0.5) / gridH) * sigH);

      const match = findBestMatch(
        sigA, sigB, sigW, sigH,
        acx, acy,
        MOTION_CONFIG.blockSize,
        MOTION_CONFIG.searchRadius,
      );
      vectors[gy * gridW + gx] = match;
    }
  }

  // 3. Median filter to reject outliers
  const filtered = medianFilterField(vectors, gridW, gridH);

  // 4. Pack into RGBA for GPU
  const { rgba, maxMagnitude } = packField(
    filtered, gridW, gridH, MOTION_CONFIG.signalDownsample,
  );

  const elapsed = performance.now() - t0;
  console.log(
    `[MotionEst] ${elapsed.toFixed(1)}ms | grid ${gridW}×${gridH} | ` +
    `maxMag ${maxMagnitude.toFixed(1)}px | sig ${sigW}×${sigH}`
  );

  return { width: gridW, height: gridH, rgba, maxMagnitude };
}

// ─── Debug visualization ───────────────────────────────────────────────────

/**
 * Render the flow field as a debug image: colored arrows overlaid on a
 * grayscale version of the signal. Returns a data URL that can be opened
 * in a new tab to inspect whether the motion estimation is producing
 * reasonable vectors.
 *
 * Invoke from the browser console:
 *   const img = debugVisualize(frameA, frameB, width, height);
 *   window.open(img);
 */
export function debugVisualize(
  frameA: Uint8Array,
  frameB: Uint8Array,
  width: number,
  height: number,
): string {
  const field = estimateMotion(frameA, frameB, width, height);
  const gridW = field.width;
  const gridH = field.height;

  // Produce a canvas that visualizes the flow field. Background: frame A's
  // signal (grayscale). Overlay: vector arrows. Upscaled 2× so arrows are
  // easier to see.
  const { signal: sigA, sigW, sigH } = extractSignal(
    frameA, width, height, MOTION_CONFIG.signalDownsample,
  );

  const scale = 2;
  const canvas = document.createElement("canvas");
  canvas.width = sigW * scale;
  canvas.height = sigH * scale;
  const ctx = canvas.getContext("2d")!;

  // Paint signal as grayscale (upscaled)
  const tmp = document.createElement("canvas");
  tmp.width = sigW; tmp.height = sigH;
  const tmpCtx = tmp.getContext("2d")!;
  const imgData = tmpCtx.createImageData(sigW, sigH);
  for (let i = 0; i < sigA.length; i++) {
    const v = sigA[i];
    imgData.data[i * 4]     = v;
    imgData.data[i * 4 + 1] = v;
    imgData.data[i * 4 + 2] = v;
    imgData.data[i * 4 + 3] = 255;
  }
  tmpCtx.putImageData(imgData, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tmp, 0, 0, canvas.width, canvas.height);

  // Log stats about the flow field
  let nonzero = 0;
  let highConf = 0;
  for (let i = 0; i < gridW * gridH; i++) {
    const r = field.rgba[i * 4];
    const g = field.rgba[i * 4 + 1];
    const b = field.rgba[i * 4 + 2];
    if (Math.abs(r - 127) > 3 || Math.abs(g - 127) > 3) nonzero++;
    if (b > 100) highConf++;
  }
  console.log(
    `[MotionDebug] grid ${gridW}×${gridH} (${gridW * gridH} cells) | ` +
    `${nonzero} nonzero vectors | ${highConf} high-confidence cells | ` +
    `max magnitude ${field.maxMagnitude.toFixed(1)} source px`
  );

  // Draw arrows
  ctx.strokeStyle = "red";
  ctx.fillStyle = "red";
  ctx.lineWidth = 2;

  for (let gy = 0; gy < gridH; gy++) {
    for (let gx = 0; gx < gridW; gx++) {
      const idx = (gy * gridW + gx) * 4;
      const r = field.rgba[idx];
      const g = field.rgba[idx + 1];
      const b = field.rgba[idx + 2];

      if (b < 10) continue; // skip truly-no-confidence cells

      // Decode displacement (source pixels, scaled down to signal pixels)
      const maxDisp = MOTION_CONFIG.maxDisplacement;
      const sxSig = ((r - 127) / 127) * maxDisp / MOTION_CONFIG.signalDownsample;
      const sySig = ((g - 127) / 127) * maxDisp / MOTION_CONFIG.signalDownsample;
      // Scale to the canvas's upscaled coordinate space
      const sx = sxSig * scale;
      const sy = sySig * scale;
      const mag = Math.sqrt(sx * sx + sy * sy);

      // Draw arrow at grid cell center
      const cx = ((gx + 0.5) / gridW) * sigW * scale;
      const cy = ((gy + 0.5) / gridH) * sigH * scale;

      ctx.globalAlpha = Math.max(0.3, b / 255);

      if (mag < 2) {
        // Near-zero motion: draw a small circle so we can SEE the grid cell
        // was evaluated (and knows there's no motion) rather than missing.
        ctx.beginPath();
        ctx.arc(cx, cy, 2, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Draw shaft
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + sx, cy + sy);
        ctx.stroke();

        // Arrowhead — scale with magnitude but cap it so tiny arrows have
        // visible heads and big arrows don't overwhelm.
        const ang = Math.atan2(sy, sx);
        const ah = Math.min(8, Math.max(4, mag * 0.3));
        ctx.beginPath();
        ctx.moveTo(cx + sx, cy + sy);
        ctx.lineTo(
          cx + sx - ah * Math.cos(ang - Math.PI / 6),
          cy + sy - ah * Math.sin(ang - Math.PI / 6),
        );
        ctx.lineTo(
          cx + sx - ah * Math.cos(ang + Math.PI / 6),
          cy + sy - ah * Math.sin(ang + Math.PI / 6),
        );
        ctx.closePath();
        ctx.fill();
      }
    }
  }
  ctx.globalAlpha = 1;

  return canvas.toDataURL("image/png");
}